import React, { useEffect, useState } from "react";
import { useTheme } from "@/context/ThemeContext";
import {
  listSessions,
  changePassword,
  twofaVerify,
  twofaDisable,
  logoutAll,
} from "@/services/auth.service";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Moon, Sun } from "lucide-react";
import { Section } from "./SettingsShared";

const SecurityTab = ({ user, refreshUser }) => {
  const { theme, toggle } = useTheme();
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [twofaOpen, setTwofaOpen] = useState(false);
  const [code, setCode] = useState("");
  const [sessions, setSessions] = useState([]);

  const loadSessions = async () => { try { setSessions(await listSessions()); } catch {} };
  useEffect(() => { loadSessions(); }, []);

  const changePw = async () => {
    if (pwForm.next !== pwForm.confirm) { toast.error("Passwords don't match"); return; }
    if (pwForm.next.length < 8) { toast.error("Min 8 characters"); return; }
    setPwSaving(true);
    try {
      await changePassword({ current_password: pwForm.current, new_password: pwForm.next });
      toast.success("Password changed");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setPwSaving(false); }
  };

  const enable2FA = async () => {
    if (!/^\d{6}$/.test(code)) { toast.error("Enter a 6-digit code"); return; }
    try {
      await twofaVerify(code);
      toast.success("2FA enabled");
      setTwofaOpen(false); setCode("");
      await refreshUser();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const disable2FA = async () => {
    try { await twofaDisable(); toast.success("2FA disabled"); await refreshUser(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const logoutAllDevices = async () => {
    try { const r = await logoutAll(); toast.success(`Revoked ${r.revoked} session(s)`); await loadSessions(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const canChangePw = user.auth_provider === "email";

  return (
    <>
      {canChangePw && (
        <Section title="Password" description="Change the password you use to sign in." data-testid="security-password-section">
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label>Current password</Label><Input type="password" value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} className="mt-1.5 h-11" data-testid="pw-current-input" /></div>
            <div><Label>New password</Label><Input type="password" value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} className="mt-1.5 h-11" data-testid="pw-new-input" /></div>
            <div><Label>Confirm</Label><Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} className="mt-1.5 h-11" data-testid="pw-confirm-input" /></div>
          </div>
          <div className="flex justify-end"><Button onClick={changePw} disabled={pwSaving} data-testid="change-pw-btn">{pwSaving ? "Saving…" : "Change password"}</Button></div>
        </Section>
      )}
      <Section title="Two-factor authentication" description="Add an extra layer of security when signing in." data-testid="security-2fa-section">
        <div className="flex items-center justify-between">
          <div className="text-sm">
            <div className="font-medium">Status: <span className={user.two_factor_enabled ? "text-emerald-600" : "text-muted-foreground"}>{user.two_factor_enabled ? "Enabled" : "Not enabled"}</span></div>
          </div>
          {user.two_factor_enabled ? (
            <Button variant="outline" onClick={disable2FA} data-testid="disable-2fa-btn">Disable 2FA</Button>
          ) : (
            <Button onClick={() => setTwofaOpen(true)} data-testid="enable-2fa-btn">Enable 2FA</Button>
          )}
        </div>
      </Section>
      <Section title="Active sessions" description="Sessions currently signed in on this account." data-testid="security-sessions-section">
        {sessions.length === 0 ? (
          <div className="text-sm text-muted-foreground">No sessions.</div>
        ) : (
          <div className="space-y-2">
            {sessions.map((s, i) => (
              <div key={i} className="flex items-center justify-between border border-border rounded-lg p-3" data-testid={`session-row-${i}`}>
                <div>
                  <div className="font-mono text-xs">{s.session_token}</div>
                  <div className="text-xs text-muted-foreground">Created {s.created_at}</div>
                </div>
                {s.is_current && <span className="text-[10px] uppercase tracking-widest font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">CURRENT</span>}
              </div>
            ))}
          </div>
        )}
        <Button variant="outline" onClick={logoutAllDevices} data-testid="logout-all-btn">Log out of all other devices</Button>
      </Section>
      <Section title="Appearance" description="Light or dark mode." data-testid="security-appearance-section">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium inline-flex items-center gap-2">
            {theme === "dark" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />} Dark mode
          </div>
          <Switch checked={theme === "dark"} onCheckedChange={toggle} data-testid="dark-mode-switch" />
        </div>
      </Section>

      <Dialog open={twofaOpen} onOpenChange={setTwofaOpen}>
        <DialogContent data-testid="2fa-modal">
          <DialogHeader><DialogTitle>Enable 2FA</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Enter the 6-digit code from your authenticator app.</p>
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" className="h-11 font-mono text-center text-2xl tracking-widest" data-testid="2fa-code-input" />
          </div>
          <DialogFooter><Button onClick={enable2FA} className="w-full" data-testid="2fa-confirm-btn">Verify & enable</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SecurityTab;
